import { logger } from '@sentry/react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { siteConfig } from '../../config/siteConfig';
import { subscriptionService } from '../../apiServices/SubscriptionService';
import Button from '../../components/Button/Button';
import { Flex } from '../../components/Flex/Flex';
import { Sizing } from '../../styles/spacing';
import { PremiumBadge } from '../../components/Icons';
import { Text } from '../../components/Text/Text';
import { FontSize } from '../../styles/typography';
import { useToast } from '../../context/Toast/useToast';
import { FeatureFlags, useFeatureFlags } from '../../hooks/useFeatureFlags';
import { PaidPlanId, siteYearlyDiscountPercent } from '@ritabygglov/core';
import { BillingPeriod, Plan, getPlans } from './plans';
import {
  BadgeContainer,
  Card,
  Cross,
  Description,
  Dots,
  FeatureRow,
  FeatureValue,
  Header,
  InputContainer,
  Period,
  Price,
  SubscriptionFooter,
  DiscountText,
} from './SubscriptionModal.styles';

export const PricingCard = ({
  plan,
  period,
  onSubscribe,
  selectLabel,
  perMonthLabel,
  perYearLabel,
  paymentLine1,
  paymentLine2,
  paymentLine3,
  termsLink,
}: {
  plan: Plan;
  period: BillingPeriod;
  onSubscribe: (planId: PaidPlanId) => void;
  selectLabel: string;
  perMonthLabel: string;
  perYearLabel: string;
  paymentLine1: string;
  paymentLine2: string;
  paymentLine3: string;
  termsLink: string;
}) => (
  <Card>
    <div>
      <Header colorScheme={plan.colorScheme}>{plan.title}</Header>
      {plan.isPremium && (
        <BadgeContainer>
          <PremiumBadge />
        </BadgeContainer>
      )}
      <Price>
        {period === 'monthly' && (
          <>
            {plan.monthlyPrice} <Period>{perMonthLabel}</Period>
          </>
        )}
        {period === 'yearly' && (
          <>
            {plan.yearlyPrice} <Period>{perYearLabel}</Period>
            {plan.monthlyEquivalentPrice && (
              <DiscountText>
                {plan.monthlyEquivalentPrice} {perMonthLabel}
              </DiscountText>
            )}
          </>
        )}
      </Price>
      <Description>{plan.description}</Description>
      {plan.features.map((feature) => (
        <FeatureRow key={feature.label}>
          <span>{feature.label}</span>
          <Dots />
          <FeatureValue>
            {feature.checked ? <>✅</> : <Cross>×</Cross>}
          </FeatureValue>
        </FeatureRow>
      ))}
    </div>
    <div>
      {!plan.isFree && (
        <>
          <Flex $padding={Sizing.sm}>
            <Button onClick={() => onSubscribe(plan.planId as PaidPlanId)}>
              {selectLabel}
            </Button>
          </Flex>
          <SubscriptionFooter>
            <div>
              {paymentLine1}
              <br />
              {paymentLine2}
              <br />
              {paymentLine3}{' '}
              <a
                href={siteConfig.termsUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {termsLink}
              </a>
              .
            </div>
          </SubscriptionFooter>
        </>
      )}
    </div>
  </Card>
);

export const SubscriptionModal = ({ showDescription = false }) => {
  const toast = useToast();
  const intl = useIntl();
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const isSandboxSubscription = useFeatureFlags(
    FeatureFlags.SandboxSubscription
  );

  const plans = getPlans(intl);
  const discountPercent = siteYearlyDiscountPercent(siteConfig.site);
  const perMonthLabel = intl.formatMessage({ id: 'subscription.perMonth' });
  const perYearLabel = intl.formatMessage({ id: 'subscription.perYear' });

  const onSubscribe = async (planId: PaidPlanId) => {
    try {
      const response = await subscriptionService.createCheckoutSession({
        isSandbox: isSandboxSubscription,
        billingPeriod,
        subscriptionType: planId,
      });
      if (response.url) {
        window.location.href = response.url;
      } else {
        throw new Error(intl.formatMessage({ id: 'subscription.noUrlError' }));
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_error) {
      logger.error('Error creating checkout session');
      toast.addToast({
        message: intl.formatMessage({ id: 'subscription.errorToast' }),
        type: 'error',
      });
    }
  };

  return (
    <div>
      <InputContainer>
        <label>
          <input
            type="radio"
            name="billingPeriod"
            checked={billingPeriod === 'monthly'}
            onChange={() => setBillingPeriod('monthly')}
          />
          {intl.formatMessage({ id: 'subscription.monthly' })}
        </label>
        <label>
          <input
            type="radio"
            name="billingPeriod"
            checked={billingPeriod === 'yearly'}
            onChange={() => setBillingPeriod('yearly')}
          />
          {intl.formatMessage({ id: 'subscription.yearly' })}{' '}
          <Text
            $color="#068051"
            $fontSize={FontSize.md}
            as="span"
            $fontWeight="bold"
          >
            {intl.formatMessage(
              { id: 'subscription.discount' },
              { percent: discountPercent }
            )}
          </Text>
        </label>
      </InputContainer>
      {showDescription && (
        <Text $padding={Sizing.sm} $fontSize={FontSize.md}>
          {intl.formatMessage({
            id: 'subscription.removeWatermarkDescription',
          })}
          <br />
          {intl.formatMessage(
            { id: 'subscription.questionsContact' },
            { contactEmail: siteConfig.contactEmail }
          )}
        </Text>
      )}
      <Flex $justifyContent="space-evenly" $alignItems="start" $gap={Sizing.lg}>
        {plans.map((plan) => (
          <PricingCard
            key={plan.planId}
            plan={plan}
            period={billingPeriod}
            onSubscribe={onSubscribe}
            perMonthLabel={perMonthLabel}
            perYearLabel={perYearLabel}
            selectLabel={intl.formatMessage({
              id: 'subscription.selectButton',
            })}
            paymentLine1={intl.formatMessage({
              id: 'subscription.paymentLine1',
            })}
            paymentLine2={intl.formatMessage({
              id: 'subscription.paymentLine2',
            })}
            paymentLine3={intl.formatMessage({
              id: 'subscription.paymentLine3',
            })}
            termsLink={intl.formatMessage({
              id: 'subscription.paymentTermsLink',
            })}
          />
        ))}
      </Flex>
    </div>
  );
};
